import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MonitorSmartphone } from 'lucide-react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { authApi } from '@/lib/auth-api';
import { getErrorMessage } from '@/lib/errors';

export function SessionsPage() {
  const queryClient = useQueryClient();
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: authApi.listSessions });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['sessions'] });
  const revoke = useMutation({ mutationFn: authApi.revokeSession, onSuccess: invalidate });
  const revokeOthers = useMutation({ mutationFn: authApi.revokeOtherSessions, onSuccess: invalidate });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>Active sessions</CardTitle>
            <CardDescription>Devices currently signed in to your account.</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => revokeOthers.mutate()}
            disabled={revokeOthers.isPending}
          >
            {revokeOthers.isPending && <Loader2 className="animate-spin" />} Sign out other devices
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {(revoke.error ?? revokeOthers.error) && (
          <Alert variant="danger">{getErrorMessage(revoke.error ?? revokeOthers.error)}</Alert>
        )}

        {sessions.isLoading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading sessions…
          </p>
        )}

        {sessions.isError && <Alert variant="danger">{getErrorMessage(sessions.error)}</Alert>}

        {sessions.data?.map((session) => (
          <div
            key={session.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/20 px-4 py-3"
          >
            <div className="flex min-w-0 items-start gap-3">
              <MonitorSmartphone className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {session.user_agent ?? 'Unknown device'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {session.ip_address ?? 'Unknown IP'} · last used{' '}
                  {new Date(session.last_used_at).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {session.current && <Badge variant="success">This device</Badge>}
              {!session.current && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => revoke.mutate(session.id)}
                  disabled={revoke.isPending}
                >
                  Revoke
                </Button>
              )}
            </div>
          </div>
        ))}

        {sessions.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">No active sessions.</p>
        )}
      </CardContent>
    </Card>
  );
}
